import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { DataTable } from '../../components/DataTable/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { dailyReportsAPI } from '../../services/dailyReportsAPI';
import { DailyReport } from '../../types/api';
import { useSnackbar } from 'notistack';

const columns = [
  {
    id: 'date',
    label: 'Datum',
    format: (value: string) => format(new Date(value), 'dd.MM.yyyy', { locale: de }),
  },
  { id: 'berichtnummer', label: 'Berichtnummer' },
  { id: 'projekt', label: 'Projekt' },
  { id: 'ort', label: 'Ort' },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => {
      switch (value) {
        case 'entwurf':
          return 'Entwurf';
        case 'eingereicht':
          return 'Eingereicht';
        case 'genehmigt':
          return 'Genehmigt';
        default:
          return value;
      }
    },
  },
  {
    id: 'created_at',
    label: 'Erstellt am',
    format: (value: string) =>
      format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: de }),
  },
];

export const DailyReportsListTagesbericht: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);

  // Rollen-basierte Berechtigungen
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure sehen nur eingeschränkte Daten
  const canSeeRestricted = isForeman || isInstaller;
  // Alle können Tagesberichte erstellen
  const canCreateReports = isAdmin || isManagement || isBauleiter || isForeman || isInstaller;
  // Nur Admin, Management & Bauleiter können alle bearbeiten/löschen
  const canEditAllReports = canSeeAll;
  const canDeleteAllReports = canSeeAll;
  // Nur Admin, Management & Bauleiter können exportieren
  const canExportReports = canSeeAll;
  
  // DEBUG: Rollen-Validierung ausgeben
  console.log('🔍 DEBUG - DailyReports - User:', user);
  console.log('🔍 DEBUG - DailyReports - User Role:', user?.role);
  console.log('🔍 DEBUG - DailyReports - Permissions:', {
    isAdmin,
    isManagement,
    isBauleiter,
    isForeman,
    isInstaller,
    canCreateReports,
    canEditAllReports,
    canDeleteAllReports,
    canExportReports
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await dailyReportsAPI.getReports();
      let filteredReports = response.items;
      
      // Vorarbeiter & Monteure sehen nur ihre eigenen Berichte
      if (canSeeRestricted && !canSeeAll) {
        filteredReports = response.items.filter(report => 
          report.created_by === user?.id || 
          report.ersteller === user?.email
        );
        console.log('🔍 DEBUG - Filtered reports for restricted user:', filteredReports.length, 'of', response.items.length);
      }
      
      setReports(filteredReports);
    } catch (error) {
      console.error('Error fetching reports:', error);
      enqueueSnackbar('Fehler beim Laden der Tagesberichte', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReport) return;
    try {
      await dailyReportsAPI.deleteReport(selectedReport.id);
      await fetchReports();
      enqueueSnackbar('Tagesbericht erfolgreich gelöscht', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting report:', error);
      enqueueSnackbar('Fehler beim Löschen des Tagesberichts', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedReport(null);
    }
  };

  const handleView = (report: DailyReport) => {
    navigate(`/tagesberichte/${report.id}`);
  };

  const handleEdit = (report: DailyReport) => {
    navigate(`/tagesberichte/${report.id}/edit`);
  };

  const handleDeleteClick = (report: DailyReport) => {
    setSelectedReport(report);
    setDeleteDialogOpen(true);
  };

  // Prüfung ob ein Bericht bearbeitet werden kann
  const canEditReport = (report: DailyReport) => {
    // Admin, Management & Bauleiter können alle Berichte bearbeiten
    if (canEditAllReports) return true;
    
    // Vorarbeiter & Monteure können nur ihre eigenen Berichte bearbeiten
    // und nur wenn Status = "entwurf"
    if (canSeeRestricted) {
      const isOwnReport = report.created_by === user?.id || 
                         report.ersteller === user?.email;
      const isDraft = report.status === 'entwurf';
      return isOwnReport && isDraft;
    }
    
    return false;
  };

  // Prüfung ob ein Bericht gelöscht werden kann
  const canDeleteReport = (report: DailyReport) => {
    // Admin, Management & Bauleiter können alle Berichte löschen
    if (canDeleteAllReports) return true;
    
    // Vorarbeiter & Monteure können keine Berichte löschen
    return false;
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" component="h1">
              Tagesberichte
            </Typography>
            {canCreateReports && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/tagesberichte/new')}
              >
                Neuer Tagesbericht
              </Button>
            )}
          </Box>

          <DataTable
            columns={columns}
            rows={reports}
            loading={loading}
            initialOrder="desc"
            persistKey="tagesberichte"
            onView={handleView}
            onEdit={(report: DailyReport) => {
              if (canEditReport(report)) {
                handleEdit(report);
              }
            }}
            onDelete={(report: DailyReport) => {
              if (canDeleteReport(report)) {
                handleDeleteClick(report);
              }
            }}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Tagesbericht löschen"
        content="Möchten Sie diesen Tagesbericht wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedReport(null);
        }}
      />
    </Box>
  );
}; 